
import {
  computed, defineComponent, reactive, ref,
} from 'vue';
import { IOption } from '@/core/models/AntDesign/IOption';
import { useTelaBase } from '@/core/composables/TelaBase';
import UtilitarioGeral from '@/core/utilitarios/UtilitarioGeral';
import storeSistema from '@/store/storeSistema';
import { IParametrosConsulta } from '@/core/models/Consulta/IParametrosConsulta';
import { IParametrosConsultaRapida } from '@/core/models/Consulta/IParametrosConsultaRapida';
import ServicoEtapaProducao from '@/servicos/Cadastros/PCP/ServicoEtapaProducao';

export default defineComponent({
  name: 'SelecionarEtapaProducao',
  props: {
    varios: {
      type: Boolean,
      default: false,
    },
    codigoSelecionado: {
      type: Number || undefined,
    },
    codigosSelecionados: {
      type: Array as () => number[],
      default: () => [],
    },
    descricaoSelecionado: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: 'Selecione',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    empresas: {
      type: Array as () => number[],
      required: true,
    },
    etapasProducao: {
      type: Array as () => number[],
    },
  },
  emits: ['update:codigoSelecionado', 'update:codigosSelecionados', 'update:descricaoSelecionado', 'change'],
  setup(props, { emit }) {
    const { montaOpcoesComListaConsultaRapida } = useTelaBase();
    const refEtapaProducao = ref<HTMLElement | null>(null);

    let debounce = 0;
    const servicoEtapaProducao = new ServicoEtapaProducao();
    servicoEtapaProducao.requisicaoSistema();

    const state = reactive({
      listaEtapas: [] as IOption[],
      buscandoDados: false,
      emDigitacao: false,
    });

    const computedDescricaoSelecionado = computed({
      get: () => props.descricaoSelecionado,
      set: (val: string) => {
        emit('update:descricaoSelecionado', val);
      },
    });

    async function obterEtapaProducaoPorCodigo(valor: any) {
      if (state.emDigitacao) { return; }
      if (!UtilitarioGeral.valorValido(valor)) { return; }
      if (!(valor > 0)) { return; }

      if (props.varios) {
        if (props.codigosSelecionados.length === 0) { return; }
      } else if (props.codigoSelecionado === 0) {
        return;
      }
      let efetuarConsulta = true;

      if (UtilitarioGeral.validaLista(state.listaEtapas)) {
        if (props.varios) {
          const listaCodigosConsulta = state.listaEtapas.map((c) => c.value);
          const diferencas = props.codigosSelecionados.filter((c) => !listaCodigosConsulta.includes(c));
          if (!UtilitarioGeral.validaLista(diferencas)) {
            efetuarConsulta = false;
          }
        } else {
          const existeLista = state.listaEtapas.find((c) => c.value === props.codigoSelecionado);
          if (existeLista !== undefined) {
            efetuarConsulta = false;
          }
        }

        const option = state.listaEtapas.find((c) => c.value === valor);
        if (option?.label !== undefined && option?.label !== '') {
          computedDescricaoSelecionado.value = option?.label;
        }
      }

      if (!efetuarConsulta) return;

      state.listaEtapas = [];

      const parametrosConsultaRapida = {} as IParametrosConsulta;

      if (UtilitarioGeral.validaLista(props.empresas)) {
        parametrosConsultaRapida.empresas = props.empresas;
      } else {
        parametrosConsultaRapida.empresas = storeSistema.getters.empresasUsuarioAutenticado();
      }

      if (UtilitarioGeral.validaLista(props.etapasProducao)) {
        parametrosConsultaRapida.codigosSelecionados = props.etapasProducao;
      }
      const listaEtapas = await servicoEtapaProducao.consultaRapida(parametrosConsultaRapida);
      state.listaEtapas = montaOpcoesComListaConsultaRapida(listaEtapas);
      if (!UtilitarioGeral.validaLista(listaEtapas)) {
        if (props.varios) {
          emit('update:codigosSelecionados', undefined);
        } else {
          emit('update:codigoSelecionado', undefined);
        }
      }
    }

    async function pesquisarEtapaProducao(valorPesquisa: any) {
      state.emDigitacao = true;

      state.buscandoDados = true;
      state.listaEtapas = [];
      state.emDigitacao = true;

      clearTimeout(debounce);
      debounce = setTimeout(async () => {
        const parametrosConsultaRapida: IParametrosConsultaRapida = {
          valor: valorPesquisa, apenasAtivos: true, recursoAssociado: '', quantidadeRegistros: 100,
        };

        if (UtilitarioGeral.validaLista(props.empresas)) {
          parametrosConsultaRapida.empresas = props.empresas;
        } else {
          parametrosConsultaRapida.empresas = storeSistema.getters.empresasUsuarioAutenticado();
        }
        const listaEtapas = await servicoEtapaProducao.consultaRapida(parametrosConsultaRapida);
        state.buscandoDados = false;
        state.listaEtapas = montaOpcoesComListaConsultaRapida(listaEtapas);
      }, 600);
    }

    function obterEtapaProducao():any {
      obterEtapaProducaoPorCodigo(props.codigoSelecionado);
      return (props.codigoSelecionado === 0 ? undefined : props.codigoSelecionado);
    }

    const computedCodigoSelecionado = computed({
      get: () => obterEtapaProducao(),
      set: (val: number) => {
        emit('update:codigoSelecionado', val);
        const option = state.listaEtapas.find((c) => c.value === val);
        if (option?.label !== undefined && option?.label !== '') {
          computedDescricaoSelecionado.value = option?.label;
        }
      },
    });

    function obterEtapasProducao():number[] {
      obterEtapaProducaoPorCodigo(props.codigosSelecionados);
      return props.codigosSelecionados;
    }

    const computedCodigosSelecionados = computed({
      get: () => obterEtapasProducao(),
      set: (val: number[]) => {
        emit('update:codigosSelecionados', val);
      },
    });

    function change() {
      emit('change', props.varios ? props.codigosSelecionados : props.codigoSelecionado);
    }

    function focus() {
      if (refEtapaProducao.value) {
        refEtapaProducao.value.focus();
      }
    }

    return {
      props,
      state,
      refEtapaProducao,
      pesquisarEtapaProducao,
      computedCodigoSelecionado,
      computedCodigosSelecionados,
      change,
      focus,
      computedDescricaoSelecionado,
    };
  },
});
